//askSmart-ui/src/pages/SmartAccess.tsx
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./SmartAccess.css";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SMRTAccess() {
  const navigate = useNavigate();
  const location = useLocation();

  const [sites, setSites] = useState<any[]>([]);
  const [selectedSite, setSelectedSite] = useState<any | null>(null);
  const [doors, setDoors] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);

  const sitesLoaded = useRef(false);
  const siteStateLoaded = useRef(false);

  // ── Fetch sites from backend ──────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/smrtaccess/sites`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setSites(data.sites || []);
        sitesLoaded.current = true;
      })
      .catch(err => console.error("Failed to load sites:", err));
  }, []);

  // ── Debounced PUT for sites ───────────────────────────────────────────────
  const sitesSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveSites = useCallback(() => {
    if (!sitesLoaded.current) return;
    if (sitesSaveTimer.current) clearTimeout(sitesSaveTimer.current);
    sitesSaveTimer.current = setTimeout(() => {
      fetch(`${API}/smrtaccess/sites`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sites }),
      }).catch(err => console.error("Failed to save sites:", err));
    }, 1000);
  }, [sites]);
  useEffect(() => { saveSites(); }, [saveSites]);

  // ── Fetch site state when site selected ───────────────────────────────────
  useEffect(() => {
    if (!selectedSite) {
      setDoors([]);
      setEvents([]);
      setNotifications([]);
      siteStateLoaded.current = false;
      return;
    }

    fetch(`${API}/smrtaccess/site-state/${encodeURIComponent(selectedSite.id)}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        let d = data.doors || [];
        // Auto-generate 10 doors if none exist
        if (d.length === 0) {
          d = Array.from({ length: 10 }, (_, i) => ({
            id: i + 1,
            label: `Door ${i + 1}`,
            name: `Door ${i + 1}`,
          }));
        }
        setDoors(d);
        setEvents(data.events || []);
        setNotifications(data.notifications || []);
        siteStateLoaded.current = true;
      })
      .catch(err => console.error("Failed to load site state:", err));
  }, [selectedSite]);

  // ── Debounced PUT for site state ──────────────────────────────────────────
  const stateSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveSiteState = useCallback(() => {
    if (!siteStateLoaded.current || !selectedSite) return;
    if (stateSaveTimer.current) clearTimeout(stateSaveTimer.current);
    stateSaveTimer.current = setTimeout(() => {
      fetch(`${API}/smrtaccess/site-state/${encodeURIComponent(selectedSite.id)}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ doors, events, notifications }),
      }).catch(err => console.error("Failed to save site state:", err));
    }, 1000);
  }, [selectedSite, doors, events, notifications]);
  useEffect(() => { saveSiteState(); }, [saveSiteState]);

  // ── Select site from navigation state ─────────────────────────────────────
  useEffect(() => {
    if (location.state?.siteId && sites.length > 0) {
      const site = sites.find(
        (s: any) => s.id.toString() === location.state.siteId.toString(),
      );
      if (site) setSelectedSite(site);
    }
  }, [location.state, sites]);

  const handleDelete = (id: number) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this site?",
    );
    if (!confirmDelete) return;

    const updated = sites.filter((site) => site.id !== id);
    setSites(updated);

    if (selectedSite?.id === id) {
      setSelectedSite(null);
    }
  };

  return (
    <div
      className="smrt-access-container"
      onClick={() => setSelectedSite(null)}
    >
      {/* LEFT — Sites */}
      <div className="smrt-access-panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel-header">
          <h3>Site Information</h3>
          <button
            className="btn-add"
            onClick={() => navigate("/smrt-access/new")}
          >
            + Add Site
          </button>
        </div>

        <div className="panel-body">
          {sites.length === 0 ? (
            <div className="empty-state">No sites added</div>
          ) : (
            sites.map((site) => (
              <div
                key={site.id}
                className={`site-item ${selectedSite?.id === site.id ? "active" : ""}`}
              >
                <div onClick={() => setSelectedSite(site)} style={{ flex: 1 }}>
                  <div>{site.name}</div>
                  <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    {site.displayName || "—"}
                  </div>
                </div>

                <div className="site-actions">
                  <button
                    className="btn-view"
                    onClick={() =>
                      navigate(`/smrt-access/new?id=${site.id}&mode=view`)
                    }
                  >
                    View
                  </button>

                  <button
                    className="btn-edit"
                    onClick={() => navigate(`/smrt-access/new?id=${site.id}`)}
                  >
                    Edit
                  </button>

                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(site.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* MIDDLE — Doors */}
      <div className="smrt-access-panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel-header">
          <h3>Device Mapping</h3>
        </div>

        <div className="panel-body">
          {!selectedSite ? (
            <div className="empty-state">Please select a site</div>
          ) : (
            doors.map((door) => (
              <div key={door.id} className="door-row">
                <div className="door-label">{door.label}</div>
                <input
                  className="door-input"
                  value={door.name}
                  onChange={(e) => {
                    const updated = doors.map((d) =>
                      d.id === door.id ? { ...d, name: e.target.value } : d,
                    );
                    setDoors(updated);
                  }}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {/* RIGHT — Users / Notifications */}
      <div className="smrt-access-panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel-header">
          <h3>Access Notifications</h3>
        </div>

        <div className="panel-body">
          {!selectedSite ? (
            <div className="empty-state">Please select a site</div>
          ) : (
            <>
              <button
                className="btn-add"
                onClick={() =>
                  navigate(`/smrt-access/notify?siteId=${selectedSite.id}`)
                }
              >
                + Add User
              </button>

              {notifications.length === 0 ? (
                <div className="empty-state">No users configured</div>
              ) : (
                notifications.map((user: any) => (
                  <div key={user.id} className="user-row">
                    <div>{user.name}</div>
                    <div>{user.phone}</div>
                    <div>
                      {(() => {
                        const selected = user.events || [];
                        if (selected.length === events.length) return "All";
                        return events
                          .filter((ev: any) => selected.includes(ev.id))
                          .map((ev: any) => ev.label)
                          .join(", ");
                      })()}
                    </div>

                    <div className="user-actions">
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(
                            `/smrt-access/notify?siteId=${selectedSite.id}&userId=${user.id}`,
                          );
                        }}
                      >
                        ✏️
                      </span>

                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const updated = notifications.filter((u: any) => u.id !== user.id);
                          setNotifications(updated);
                        }}
                      >
                        🗑️
                      </span>
                    </div>
                  </div>
                ))
              )}
              <hr style={{ margin: "12px 0", borderColor: "#334155" }} />

              <div style={{ marginTop: "8px" }}>
                <h4 style={{ marginBottom: "6px" }}>Event Types</h4>

                <button
                  className="btn-add"
                  onClick={() => {
                    const label = prompt("Enter event name");
                    if (!label) return;

                    const hasAll = events.some((e: any) => e.id === "all");
                    const base = hasAll
                      ? events.filter((e: any) => e.id !== "all")
                      : events;

                    setEvents([
                      { id: "all", label: "All" },
                      ...base,
                      { id: Date.now().toString(), label },
                    ]);
                  }}
                >
                  + Add Event
                </button>

                <div style={{ marginTop: "6px" }}>
                  {events.map((ev: any) => (
                    <div
                      key={ev.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "4px 6px",
                        background: "#0f172a",
                        borderRadius: "6px",
                        marginTop: "4px",
                      }}
                    >
                      <span>{ev.label}</span>

                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setEvents(events.filter((e: any) => e.id !== ev.id));
                        }}
                      >
                        🗑️
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
